import React, { useState, useEffect, useRef } from "react";
import { AiOutlineReload, AiFillCopy } from "react-icons/ai";
import Input from "../../UI/Input";
import Button from "../../UI/Button";
import { createBotAvatar } from "../../../utils/globals/avatarGenerator";
import {
  getUser as getAuthUser,
  setUser as setAuthUser,
} from "../../../utils/globals/authUser";
import useForm from "../../Hooks/hook-form";
import { userProfile as userProfileSchema } from "../../../utils/schemas/user.schema";
import "./profile.css";

const Profile = ({
  user,
  userMeetingId,
  showEditMode,
  editMode,
  showCancelEditButton = true,
  onEditModeChange,
  onProfileChange,
  className,
}) => {
  const userAvatar = useRef();
  const [_user, setUser] = useState(null);
  const [_userMeetingId, setUserMeetingId] = useState(null);
  const [_copyingMeetingIdToClipboard, setCopyingMeetingIdToClipboard] =
    useState(false);
  const [_showEditMode, setShowEditMode] = useState(true);
  const [_editMode, setEditMode] = useState(false);

  const { register, handleSubmit, errors, setValue } = useForm({
    schema: userProfileSchema,
  });

  const getNewAvatar = (originalFormula = {}) => {
    const formula = {
      seed: "bot-" + Math.random() * 1000,
      size: 150,
      ...originalFormula, // will override above
    };
    const avatar = createBotAvatar(formula);
    return { avatar, formula };
  };

  const handleEditProfileClick = () => {
    onEditModeChange && onEditModeChange(_editMode);

    setEditMode((oldMode) => !oldMode);
  };

  const handleCancelEditProfileClick = () => {
    setEditMode((oldMode) => !oldMode);
  };

  const handleChangeAvatarClick = () => {
    const newAvatar = getNewAvatar();

    // update avatar value for form
    setValue("avatar", newAvatar.avatar);

    // update avatar wrapper element
    userAvatar.current.innerHTML = newAvatar.avatar;

    // update state
    setUser({
      ..._user,
      avatar: newAvatar.avatar,
      avatarFormula: newAvatar.formula,
    });
  };

  const handleClipboardIconClick = () => {
    if (_userMeetingId) {
      setCopyingMeetingIdToClipboard(true);
      setTimeout(function () {
        setCopyingMeetingIdToClipboard(false);
      }, 500);

      const msg = `Hi, ${_user.name} Here, Call me using this link.\nLink: ${window.location.origin}/?call=${_userMeetingId}`;
      navigator.clipboard.writeText(msg);
    }
  };

  const onSubmit = async (data) => {
    // new user obj
    const _newUser = {
      name: data?.name,
      avatar: data?.avatar,
      avatarFormula: _user?.avatarFormula,
    };

    // update state values
    setUser(_newUser);
    setEditMode(false);

    // update avatar wrapper element
    userAvatar.current.innerHTML = data?.avatar;

    // save user obj in browser storage
    setAuthUser({ name: data?.name, avatarFormula: _user?.avatarFormula });

    // trigger change handler
    onProfileChange && onProfileChange(_newUser);
  };

  const setUserFromProps = (user) => {
    const _name = user?.name || "";
    const { _avatar, _avatarFormula } = (() => {
      const tempAvatar = user.avatar;
      const tempAvatarFormula = user.avatarFormula;
      if (!tempAvatar || !tempAvatarFormula) {
        const newAvatar = getNewAvatar();
        return {
          _avatar: newAvatar.avatar,
          _avatarFormula: newAvatar.formula,
        };
      }
      return {
        _avatar: tempAvatar,
        _avatarFormula: tempAvatarFormula,
      };
    })();

    // update form values
    setValue("name", _name);
    setValue("avatar", _avatar);

    // update avatar wrapper element
    userAvatar.current.innerHTML = _avatar;

    // update state
    setUser({
      name: _name,
      avatar: _avatar,
      avatarFormula: _avatarFormula,
    });
  };

  useEffect(() => {
    if (user?.name || user?.avatarFormula) {
      setUserFromProps(user);
    } else {
      const _user = getAuthUser() || {};

      // validate name and avatar
      _user.name = _user?.name || "";
      if (!_user?.avatarFormula) {
        const newAvatar = getNewAvatar();
        _user.avatar = newAvatar.avatar;
        _user.avatarFormula = newAvatar.formula;
      } else {
        const { avatar } = getNewAvatar(_user?.avatarFormula);
        _user.avatar = avatar;
      }

      // update form values
      setValue("name", _user?.name);
      setValue("avatar", _user?.avatar);

      // update avatar wrapper element
      userAvatar.current.innerHTML = _user?.avatar;

      // update state and auth
      setUser({ ..._user });
    }
  }, []);

  useEffect(() => {
    if (user?.name || user?.avatarFormula) {
      setUserFromProps(user);
    }
  }, [user]);

  useEffect(() => {
    setUserMeetingId(userMeetingId);
  }, [userMeetingId]);

  useEffect(() => {
    if (typeof editMode === "boolean") {
      setEditMode(editMode);
    }
  }, [editMode]);

  useEffect(() => {
    if (typeof showEditMode === "boolean") {
      setShowEditMode(showEditMode);
    }
  }, [showEditMode]);

  return (
    <div
      className={`w-full max-w-md m-auto bg-gray-800 rounded-xl p-7 pt-4 flex flex-col items-center ${className}`}
    >
      {_editMode && <h3 className="text-3xl font-medium">Your Profile</h3>}
      <span className={_editMode ? "mt-5" : "mt-1"} ref={userAvatar}></span>
      {_editMode && (
        <>
          <Button className="!w-auto">
            <AiOutlineReload
              size="36"
              className="cursor-pointer"
              onClick={handleChangeAvatarClick}
            />
          </Button>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full">
            <Input
              className={`mt-4 bg-gray-900 ${errors?.name && `border-2 border-red-400 focus:border-red-400`
                }`}
              placeholder="Your Name"
              customProps={{ ...register("name") }}
            />
            {errors?.name?.message && (
              <p className="text-red-400">{errors?.name?.message}</p>
            )}
            <div className="flex gap-4">
              {showCancelEditButton && <Button className="mt-4 bg-red-600 py-2 px-5" type="button" onClick={handleCancelEditProfileClick}>Cancel</Button>}
              <Button className="mt-4 bg-green-700 py-2 px-5">Continue</Button>
            </div>
          </form>
        </>
      )}
      {!_editMode && (
        <>
          <h3 className="mt-1 text-2xl">{_user?.name}</h3>
          {_userMeetingId && (
            <p className="mt-3 text-gray-200 bg-gray-900 px-2 py-1 rounded-xl flex justify-center items-center">
              Meeting Id: {_userMeetingId}{" "}
              <Button className="!w-auto ml-4 !p-0">
                <AiFillCopy
                  size="20"
                  className={`cursor-pointer fill-current text-green-400 ${_copyingMeetingIdToClipboard &&
                    `animate-bounce duration-100`
                    }`}
                  onClick={handleClipboardIconClick}
                />
              </Button>
            </p>
          )}
          {_showEditMode && (
            <Button
              className="mt-6 bg-green-700 flex justify-center items-center py-2 px-5"
              onClick={handleEditProfileClick}
            >
              Edit Profile
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default Profile;
